import { useState } from 'react';
import { Container, Group, Title, Text, TextInput, Card, Badge, Button, Skeleton, SimpleGrid } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconArrowRight } from '@tabler/icons-react';
import { useSubstitutionSuggestions, useAcceptSubstitution } from '@/hooks/usePortalApi';

export default function SmartSubstitutions() {
  const [productId, setProductId] = useState<string>('');
  const [searchValue, setSearchValue] = useState('');

  const { data: suggestions, isLoading } = useSubstitutionSuggestions(productId ? parseInt(productId) : null);
  const acceptSubstitution = useAcceptSubstitution();

  const fmt = (n: number) => '$' + n.toFixed(2);

  const handleSearch = () => {
    setProductId(searchValue);
  };

  const handleAccept = async (sub: any) => {
    try {
      await acceptSubstitution.mutateAsync({
        original_product_id: sub.original_product_id,
        substitute_product_id: sub.suggested_product_id,
      });
      notifications.show({
        title: 'Success',
        message: 'Substitution accepted',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to accept substitution',
        color: 'red',
      });
    }
  };

  const handleDecline = async (_sub: any) => {
    notifications.show({
      title: 'Declined',
      message: 'Substitution declined',
      color: 'blue',
    });
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Smart Substitutions</Title>
          <Text c="dimmed" size="sm">AI-suggested product alternatives</Text>
        </div>
      </Group>

      <TextInput
        placeholder="Enter product ID to find substitutions"
        leftSection={<IconSearch size={16} />}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        mb="lg"
      />

      {isLoading ? (
        <SimpleGrid cols={1}>
          {[1, 2].map((i) => (
            <Card key={i} shadow="sm" padding="lg" withBorder>
              <Skeleton height={20} width="40%" mb="sm" />
              <Skeleton height={60} mb="sm" />
              <Skeleton height={30} width="50%" />
            </Card>
          ))}
        </SimpleGrid>
      ) : !suggestions || suggestions.length === 0 ? (
        <Text ta="center" c="dimmed" py="xl">
          {productId ? 'No substitution suggestions available' : 'Enter a product ID to search'}
        </Text>
      ) : (
        <SimpleGrid cols={1}>
          {suggestions.map((sub: any) => (
            <Card key={sub.id} shadow="sm" padding="lg" withBorder>
              <Group mb="md" align="center">
                <div style={{ flex: 1 }}>
                  <Text fw={500}>{sub.original_product_name}</Text>
                  <Text size="sm" c="dimmed">{fmt(sub.original_price)}</Text>
                </div>

                <IconArrowRight size={24} />

                <div style={{ flex: 1 }}>
                  <Text fw={500}>{sub.suggested_product_name}</Text>
                  <Text size="sm" c="dimmed">{fmt(sub.suggested_price)}</Text>
                </div>

                <div>
                  <Badge color="blue" variant="light" size="lg">
                    {sub.score}% match
                  </Badge>
                </div>
              </Group>

              <Group mb="md">
                <Text size="sm">
                  Price difference: {fmt(Math.abs(sub.suggested_price - sub.original_price))}
                  {sub.suggested_price < sub.original_price ? ' (cheaper)' : ' (more expensive)'}
                </Text>
                <Badge color={sub.in_stock ? 'green' : 'red'} variant="light">
                  {sub.in_stock ? 'In Stock' : 'Out of Stock'}
                </Badge>
              </Group>

              <Group>
                <Button
                  color="green"
                  onClick={() => handleAccept(sub)}
                  loading={acceptSubstitution.isPending}
                >
                  Accept
                </Button>
                <Button
                  color="gray"
                  variant="outline"
                  onClick={() => handleDecline(sub)}
                  loading={acceptSubstitution.isPending}
                >
                  Decline
                </Button>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Container>
  );
}
